import { useCallback, useEffect, useState } from "react";
import { Webhook, Loader2, CheckCircle2, AlertTriangle, RefreshCw, PlayCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const API = "/api";
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "never");

const describeResult = (r) => {
  if (!r.ok) return `failed: ${r.error}`;
  if (r.activated) return "plan activated";
  if (r.duplicate) return r.cancelled_subscription_id ? "duplicate stopped" : "duplicate (already handled)";
  if (r.already_active) return "already active";
  if (r.downgraded) return "account downgraded";
  if (r.discount_applied) return "referral discount applied";
  if (r.skipped) return `nothing to do (${r.skipped})`;
  if (r.matched_user === false) return "no matching account";
  return "done";
};

function Undelivered({ items, busyId, onReplay }) {
  return (
    <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-1" data-testid="webhook-undelivered-list">
      {items.map((u) => (
        <li key={u.event_id} className="bg-card rounded-lg border border-border/60 px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`webhook-undelivered-${u.event_id}`}>
          <Badge variant="outline" className="text-[10px]">{u.type}</Badge>
          <span className="font-medium">{u.email || u.object_id}</span>
          {u.plan && <span className="text-muted-foreground">{u.plan}</span>}
          <span className="text-muted-foreground">{fmtWhen(u.created)}</span>
          <code className="text-muted-foreground">{u.event_id}</code>
          <Button size="sm" variant="outline" className="h-7 px-2 ml-auto gap-1" disabled={!!busyId} onClick={() => onReplay(u.event_id)} data-testid={`webhook-replay-${u.event_id}`}>
            {busyId === u.event_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />} Replay
          </Button>
        </li>
      ))}
    </ul>
  );
}

export function StripeWebhookHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null); // event id or "all"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/stripe/webhook-health`, { credentials: "include" });
      if (res.ok) setHealth(await res.json());
    } catch { /* keep previous */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const replayOne = async (eventId) => {
    setBusyId(eventId);
    try {
      const res = await fetch(`${API}/admin/stripe/replay-event`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event_id: eventId }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Replay failed"); return; }
      (data.ok ? toast.success : toast.error)(`${data.type}: ${describeResult(data)}`);
      await load();
    } catch { toast.error("Replay failed"); } finally { setBusyId(null); }
  };

  const replayAll = async () => {
    setBusyId("all");
    try {
      const res = await fetch(`${API}/admin/stripe/replay-undelivered`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Replay failed"); return; }
      const failed = data.replayed.filter((r) => !r.ok).length;
      (failed ? toast.error : toast.success)(`Replayed ${data.replayed.length} event${data.replayed.length === 1 ? "" : "s"}${failed ? ` - ${failed} failed` : ""}`);
      setHealth(data.health);
    } catch { toast.error("Replay failed"); } finally { setBusyId(null); }
  };

  const rejectedAfterLastOk = health?.last_rejected_at && health.last_rejected_at > (health.last_received_at || "");

  return (
    <div className="border border-border rounded-xl p-4" data-testid="stripe-webhook-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-semibold text-lg mb-1 flex items-center gap-2"><Webhook className="w-4 h-4 text-violet-500" /> Stripe Webhook</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Stripe tells this server about every payment, renewal and cancellation. If those messages are rejected, paid users stay on Free. Plans still activate on the thank-you page, but renewals and cancellations would be missed.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2" data-testid="webhook-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Re-check
        </Button>
      </div>

      {!health ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="webhook-loading"><Loader2 className="w-4 h-4 animate-spin" /> Asking Stripe…</p>
      ) : (
        <div className="space-y-3">
          <div className={`border rounded-lg p-3 text-sm flex items-start gap-2 ${health.healthy ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400" : "bg-red-50/70 dark:bg-red-900/10 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"}`} data-testid={health.healthy ? "webhook-health-ok" : "webhook-health-bad"}>
            {health.healthy ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            <div className="space-y-0.5">
              <p className="font-medium">{health.healthy ? "Webhook is healthy" : "Webhook needs attention"}</p>
              <p className="text-xs opacity-90">Last event accepted: {fmtWhen(health.last_received_at)}{health.last_event_type ? ` (${health.last_event_type})` : ""} · {health.received_count || 0} accepted, {health.rejected_count || 0} rejected in total</p>
              {rejectedAfterLastOk && <p className="text-xs opacity-90" data-testid="webhook-last-rejected">Last rejected {fmtWhen(health.last_rejected_at)}: {health.last_reject_reason}</p>}
              {health.stripe_error && <p className="text-xs opacity-90">Stripe lookup failed: {health.stripe_error}</p>}
            </div>
          </div>

          {health.endpoints?.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="webhook-endpoints">
              {health.endpoints.map((e) => <span key={e.id} className="mr-3"><code>{e.url}</code> <Badge variant="outline" className="text-[10px] ml-1 capitalize">{e.status}</Badge></span>)}
            </p>
          )}

          {health.undelivered_count > 0 && (
            <div className="border rounded-lg p-3 bg-amber-50/60 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800" data-testid="webhook-undelivered">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">{health.undelivered_count} Stripe event{health.undelivered_count === 1 ? "" : "s"} not yet accepted by this server</p>
                <Button size="sm" onClick={replayAll} disabled={!!busyId} className="gap-1.5" data-testid="webhook-replay-all-btn">
                  {busyId === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Replay all here
                </Button>
              </div>
              <Undelivered items={health.undelivered} busyId={busyId} onReplay={replayOne} />
              <p className="text-xs text-muted-foreground mt-2"><strong>Replay</strong> fetches the event straight from Stripe and applies it exactly as the webhook would - safe to repeat, nothing double-applies. Oldest first is the right order; <em>Replay all</em> does that for you.</p>
            </div>
          )}

          {health.recent_replays?.length > 0 && (
            <details className="text-xs text-muted-foreground" data-testid="webhook-recent-replays">
              <summary className="cursor-pointer select-none">Replayed here ({health.recent_replays.length})</summary>
              <ul className="mt-2 space-y-1">
                {health.recent_replays.map((r) => (
                  <li key={r.event_id} className="flex gap-2 flex-wrap"><span>{fmtWhen(r.replayed_at)}</span><span className="font-medium text-foreground">{r.type}</span><span>{describeResult(r.result || {})}</span><code>{r.event_id}</code></li>
                ))}
              </ul>
            </details>
          )}

          {!health.healthy && (
            <p className="text-xs text-muted-foreground" data-testid="webhook-fix-hint">
              If Stripe shows "Invalid signature" on recent deliveries: Stripe Dashboard → Developers → Webhooks → the codingdrills.au endpoint → <em>Reveal signing secret</em>, save it as <code>STRIPE_WEBHOOK_SECRET</code> in the deployment secrets and redeploy. Then make sure the endpoint is <em>Enabled</em>.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
